// Custom builder for Service Events history rows
// Focused, readable diffs: Removed, Revived (deleted toggle), Customer Required, Schedule summary
/* eslint-disable no-continue, no-restricted-syntax -- ported imperative diff loop; early-continue reads clearer than array chains here */

import { sortBy, isEqual } from 'lodash'

import ActivityTreeBuilder, { isEmpty, type RevisionLike } from 'utils/activity/ActivityTreeBuilder'

type EventRevision = RevisionLike & {
  deleted?: boolean | null
  actionType?: string | null
  customerRequired?: boolean | null
  dayOfTheMonth?: number | string | null
  eventOccurrence?: number | string | null
  dayOfTheWeek?: string | string[] | null
}

type ServiceEventsPayload = { eventsById?: Record<string, EventRevision[]> }

const WEEK_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const WEEK_NAME_BY_UPPERCASE: Record<string, string> = {
  MONDAY: 'Monday',
  TUESDAY: 'Tuesday',
  WEDNESDAY: 'Wednesday',
  THURSDAY: 'Thursday',
  FRIDAY: 'Friday',
  SATURDAY: 'Saturday',
  SUNDAY: 'Sunday',
}
const WEEK_ABBREVIATIONS: Record<string, string> = {
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
  Sunday: 'Sun',
}

const normalizeDayName = (dayName: string | null | undefined): string => {
  if (!dayName) return ''
  const trimmed = String(dayName).trim()
  if (!trimmed) return ''
  const uppercase = trimmed.toUpperCase()
  if (WEEK_NAME_BY_UPPERCASE[uppercase]) return WEEK_NAME_BY_UPPERCASE[uppercase]
  // Handle proper case already or mixed
  const capitalized = trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase()
  if (WEEK_ORDER.includes(capitalized)) return capitalized
  return capitalized
}

const orderDays = (days: string[]): string[] => {
  const uniqueDays = Array.from(new Set(days.filter(Boolean).map(normalizeDayName)))
  return uniqueDays.sort((first, second) => WEEK_ORDER.indexOf(first) - WEEK_ORDER.indexOf(second))
}

const ordinal = (value: number): string => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) return ''
  const lastDigit = parsed % 10
  const lastTwoDigits = parsed % 100
  if (lastDigit === 1 && lastTwoDigits !== 11) return `${parsed}st`
  if (lastDigit === 2 && lastTwoDigits !== 12) return `${parsed}nd`
  if (lastDigit === 3 && lastTwoDigits !== 13) return `${parsed}rd`
  return `${parsed}th`
}

const occurrenceLabel = (occurrence: number): string => {
  const parsed = Number(occurrence)
  if (parsed === -1) return 'Last'
  if (parsed >= 1 && parsed <= 4) return ordinal(parsed)
  return ''
}

export const buildEventScheduleLabel = (revision: EventRevision | null | undefined): string => {
  if (!revision) return ''
  // Monthly precedence
  const dayOfMonth = Number(revision?.dayOfTheMonth || 0)
  const occurrence = Number(revision?.eventOccurrence || 0)
  const dayOfWeek = revision?.dayOfTheWeek
  if (dayOfMonth > 0) {
    return `Monthly: ${ordinal(dayOfMonth)}`
  }
  if (occurrence !== 0 && dayOfWeek) {
    const day = normalizeDayName(Array.isArray(dayOfWeek) ? dayOfWeek[0] : dayOfWeek)
    const occurrenceText = occurrenceLabel(occurrence)
    if (occurrenceText && day) return `Monthly: ${occurrenceText} ${WEEK_ABBREVIATIONS[day] || day}`
  }
  // Weekly label from days
  let days: string[]
  if (Array.isArray(dayOfWeek)) days = dayOfWeek
  else if (dayOfWeek) days = [dayOfWeek]
  else days = []
  const ordered = orderDays(days)
  if (ordered.length) return `Weekly: ${ordered.join(', ')}`
  return ''
}

const getChangeTypeStrict = (previousLabel: string, currentLabel: string): string => {
  const previousEmpty = !previousLabel || previousLabel === 'None'
  const currentEmpty = !currentLabel || currentLabel === 'None'
  if (previousEmpty && !currentEmpty) return 'Added'
  if (!previousEmpty && currentEmpty) return 'Deleted'
  return 'Updated'
}

export default function serviceEventsBuilder(builder: ActivityTreeBuilder, payload: unknown = {}): void {
  const { eventsById = {} } = (payload && typeof payload === 'object' ? payload : {}) as ServiceEventsPayload
  const entries = Object.entries(eventsById)
  for (const [, revisions] of entries) {
    if (!Array.isArray(revisions) || revisions.length === 0) continue
    const sorted = sortBy(revisions, revision => Number(revision?.rev ?? 0)) // ASC
    for (let index = 0; index < sorted.length; index++) {
      const current = sorted[index]
      const previous = sorted[index - 1]
      const base: EventRevision = !previous || String(current?.revType).toUpperCase() === 'INSERT' ? {} : previous
      const rev = current?.rev ?? index

      // No INSERT baseline (record predates auditing) — show one marker, not a full dump.
      if (!previous && String(current?.revType).toUpperCase() === 'UPDATE') {
        builder.addCustomChange(rev, current, {
          field: 'Event — earliest recorded change',
          fieldKey: '__baseline__',
          changeType: 'Baseline',
          oldValue: undefined,
          newValue: undefined,
        })
        continue
      }

      const eventLabel = 'Event' // Action suffix is formatted by renderer/registry if desired

      // Event context per row so the log says WHICH event changed: actionDate (One Off only)
      // + BE workOrderNumber. Fall back to base so sparse UPDATE/DELETE revisions (Envers can
      // omit unchanged fields) keep the value instead of dropping it from the row.
      const eventMeta = {
        eventDate: current?.actionDate ?? base?.actionDate ?? null,
        workOrderNumber: current?.workOrderNumber ?? base?.workOrderNumber ?? null,
      }

      // Hard delete or deleted flag flipped to true → Removed
      const hardDelete = String(current?.revType).toUpperCase() === 'DELETE'
      const softDelete = !!current?.deleted && !base?.deleted
      if (hardDelete || softDelete) {
        builder.addCustomChange(rev, current, {
          field: eventLabel,
          fieldKey: 'event.removed',
          changeType: 'Removed',
          oldValue: '',
          newValue: '',
          ...eventMeta,
        })
        continue
      }

      // Revived from deleted
      const revived = !current?.deleted && !!base?.deleted
      if (revived) {
        builder.addCustomChange(rev, current, {
          field: `${eventLabel} deleted`,
          fieldKey: 'deleted',
          changeType: 'Deleted',
          oldValue: true,
          newValue: false,
          ...eventMeta,
        })
      }

      // Event Action (Added/Deleted/Updated)
      // Legacy parity: show when the action type value actually changes
      {
        const previousAction = base?.actionType
        const currentAction = current?.actionType
        const bothEmpty = isEmpty(previousAction) && isEmpty(currentAction)
        const equal = previousAction === currentAction
        if (!bothEmpty && !equal) {
          let changeType = 'Updated'
          if (isEmpty(previousAction) && !isEmpty(currentAction)) changeType = 'Added'
          else if (!isEmpty(previousAction) && isEmpty(currentAction)) changeType = 'Deleted'

          builder.addCustomChange(rev, current, {
            field: `${eventLabel} - Action`,
            fieldKey: 'actionType',
            changeType,
            oldValue: isEmpty(previousAction) ? 'None' : previousAction,
            newValue: isEmpty(currentAction) ? 'None' : currentAction,
            ...eventMeta,
          })
        }
      }

      // Customer Required (suppress false->false and undefined->false noise)
      {
        const previousRequired = !!base?.customerRequired
        const currentRequired = !!current?.customerRequired
        if (previousRequired !== currentRequired) {
          let changeType = 'Updated'
          if (!previousRequired && currentRequired) changeType = 'Added'
          else if (!currentRequired && previousRequired) changeType = 'Deleted'
          builder.addCustomChange(rev, current, {
            field: `${eventLabel} - Customer required`,
            fieldKey: 'customerRequired',
            changeType,
            oldValue: previousRequired,
            newValue: currentRequired,
            ...eventMeta,
          })
        }
      }

      // Schedule summary (Monthly only). Suppress per-event Weekly rows to avoid spam;
      // the revision-level union of weekly days is handled by recurringDaysUnionBuilder.
      const isMonthly = (candidate: EventRevision): boolean => {
        const dayOfMonth = Number(candidate?.dayOfTheMonth || 0)
        const occurrence = Number(candidate?.eventOccurrence || 0)
        const dayOfWeek = candidate?.dayOfTheWeek
        return dayOfMonth > 0 || (occurrence !== 0 && !!dayOfWeek)
      }
      const oldSchedule = buildEventScheduleLabel(base)
      const newSchedule = buildEventScheduleLabel(current)
      const monthlyInvolved = isMonthly(base) || isMonthly(current)
      if (monthlyInvolved && !isEqual(oldSchedule, newSchedule)) {
        builder.addCustomChange(rev, current, {
          field: `${eventLabel} - Schedule`,
          fieldKey: 'event.schedule',
          changeType: getChangeTypeStrict(oldSchedule, newSchedule),
          oldValue: oldSchedule || 'None',
          newValue: newSchedule || 'None',
          ...eventMeta,
        })
      }
    }
  }
}
